import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, XCircle } from "lucide-react";
import { setAccessToken } from "@/api/client";
import { getMe } from "@/api/auth";
import { useAuth } from "@/hooks/useAuth";

/**
 * Lands on `/sso/callback#access_token=...&next=...`. The backend redirected
 * here after a successful OAuth / SAML assertion; it already set the
 * httpOnly refresh cookie. We lift the access token out of the URL fragment
 * (fragments never leave the browser), store it, hydrate the current user,
 * and redirect to the saved next path.
 */
export function SSOCallbackPage() {
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const hash = window.location.hash.replace(/^#/, "");
    const params = new URLSearchParams(hash);
    const token = params.get("access_token");

    // Sanitize the post-login redirect target. Anything we hand to
    // ``navigate`` that isn't a same-origin path becomes an open
    // redirect — phishing flow: legitimate-looking SSO link with
    // ``?next=//evil.com/login`` lands the victim on a Lenavio
    // lookalike. Accept only paths that start with a single ``/``
    // (block protocol-relative ``//`` and absolute URLs).
    const rawNext = params.get("next") || "/";
    const nextPath =
      rawNext.startsWith("/") && !rawNext.startsWith("//") ? rawNext : "/";

    if (!token) {
      setError("No access token in callback URL");
      return;
    }

    setAccessToken(token);

    getMe()
      .then(async () => {
        await refreshUser();
        // Strip the hash so refreshes don't re-run this.
        window.history.replaceState(null, "", window.location.pathname);
        navigate(nextPath, { replace: true });
      })
      .catch(() => {
        setAccessToken(null);
        setError("Unable to load session after SSO. Please try again.");
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-6 py-12">
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-sm text-center"
      >
        <div className="mb-8">
          <img src="/logo.png" alt="Lenavio" className="h-9 w-auto mx-auto" />
        </div>

        {!error ? (
          <div className="rounded-lg bg-surface border border-border px-6 py-8">
            <Loader2 className="h-8 w-8 text-accent mx-auto mb-3 animate-spin" />
            <p className="text-sm text-text-primary font-medium">
              Finalizing sign-in…
            </p>
          </div>
        ) : (
          <div className="rounded-lg bg-danger/10 border border-danger/20 px-6 py-8">
            <XCircle className="h-10 w-10 text-danger mx-auto mb-3" />
            <p className="text-sm text-text-primary font-medium">
              Sign-in failed
            </p>
            <p className="text-xs text-text-muted mt-1">{error}</p>
            <a
              href="/login"
              className="inline-block mt-4 text-sm text-accent hover:text-accent-600"
            >
              Back to sign in
            </a>
          </div>
        )}
      </motion.div>
    </div>
  );
}
